import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { getApiUrl } from "../../../../utils/baseUrl";
import { apolloDropdownDataHandler } from "@redux/ProfileContacts/profileContactsSlice";
import { trademoTrackingEvents } from "../../../../core/global/trademo-tracking";
import { generateTrackingObject } from "../../../../core/global/global-country-functions";
import trackingEventDictionary from "../../../../core/dictionary/tracking-event";
import styles from "./styles/new-contact-form.module.scss";
import { Search } from "react-feather";
import ContactFiltersDropdown from "./ContactFiltersDropdown";
import { captureException } from "@sentry/react";

const NewContactForm = ({ clearFilter, searchButtonCalled, dataSource, pathName, currentUserPlan, eventName = "", dropdownData: initialDropdownData }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const handleInputChange = (searchValue) => {
    setSearchTerm(searchValue);
  };

  useEffect(() => {
    if (clearFilter) {
      setDepartments([]);
      setSenority([]);
      setLocation([]);
      setSearchTerm("");
    }
  }, [clearFilter]);

  const handleKeyInput = (e) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  const dispatch = useDispatch();
  const { profileContactData } = useSelector(
    (state) => state,
    (prev, next) => prev.profileContactData === next.profileContactData
  );

  const [departmentsList, setDepartmentsList] = useState(() => (initialDropdownData?.department && [...initialDropdownData.department]) || []);
  const [senorityList, setSenorityList] = useState(() => (initialDropdownData?.seniority && [...initialDropdownData.seniority]) || []);
  const [locationList, setLocationList] = useState(() => (initialDropdownData?.location && [...initialDropdownData.location]) || []);

  const [departments, setDepartments] = useState([]);
  const [senority, setSenority] = useState([]);
  const [location, setLocation] = useState([]);

  useEffect(() => {
    const fetchDropdownData = () => {
      if (profileContactData?.apolloDropdownData && Object.keys(profileContactData.apolloDropdownData).length > 0) {
        const { department, seniority, location } = profileContactData.apolloDropdownData;

        setDepartmentsList((prevDept) => (JSON.stringify(prevDept) !== JSON.stringify(department) ? department : prevDept));
        setSenorityList((prevSen) => (JSON.stringify(prevSen) !== JSON.stringify(seniority) ? seniority : prevSen));
        setLocationList((prevLoc) => {
          const processedLocation = location.map((item) => ({ label: item, value: item }));
          return JSON.stringify(prevLoc) !== JSON.stringify(processedLocation) ? processedLocation : prevLoc;
        });
      } else {
        axios
          .get(getApiUrl("CompanyContactDetails/contactApolloDropdown"))
          .then(({ data: { message } }) => {
            dispatch(apolloDropdownDataHandler(message));

            setDepartmentsList(message.department);
            setSenorityList(message.seniority);
            setLocationList(message.location.map((item) => ({ label: item, value: item })));
          })
          .catch((error) => {
            captureException(error);
          });
      }
    };

    fetchDropdownData();
  }, [dispatch, profileContactData]);

  const departmentOptions = useMemo(
    () =>
      departmentsList.map((dep) => ({
        label: dep.label || dep,
        value: dep.value || dep,
      })),
    [departmentsList]
  );

  const seniorityOptions = useMemo(
    () =>
      senorityList.map((sen) => ({
        label: sen.label || sen,
        value: sen.value || sen,
      })),
    [senorityList]
  );

  const handleDepartmentChange = useCallback((selectedOptions) => {
    setDepartments(selectedOptions || []);
  }, []);

  const handleSeniorityChange = useCallback((selectedOptions) => {
    setSenority(selectedOptions || []);
  }, []);

  const handleLocationChange = useCallback((selectedOptions) => {
    setLocation(selectedOptions || []);
  }, []);

  const handleSearchClick = useCallback(
    (overrideParams = {}) => {
      const searchParams = {
        department: overrideParams.department !== undefined ? overrideParams.department : departments,
        senority: overrideParams.senority !== undefined ? overrideParams.senority : senority,
        location: overrideParams.location !== undefined ? overrideParams.location : location,
        job_title: overrideParams.job_title !== undefined ? overrideParams.job_title : searchTerm !== "" ? [searchTerm] : [],
      };
      trademoTrackingEvents(eventName ?? trackingEventDictionary.companyProfilePageSearchContactsTabSearchContacts, generateTrackingObject(dataSource, pathName, currentUserPlan));
      searchButtonCalled({
        department: searchParams.department,
        seniority: searchParams.senority,
        location: searchParams.location,
        job_title: searchParams.job_title,
      });
    },
    [departments, senority, searchTerm, location, eventName, dataSource, pathName, currentUserPlan, searchButtonCalled]
  );

  return (
    <div>
      <div className={`tw-flex tw-w-full tw-items-center ${styles.select_container}`}>
        <div className="tw-w-[300px] tw-flex-shrink-0 tw-relative">
          <Search size={14} color="#101828" className="tw-absolute tw-top-2.5 tw-left-3" />
          <input type="text" className="tw-w-full tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-pl-8 tw-pr-8 tw-bg-white tw-text-sm tw-text-gray-700 placeholder:tw-text-gray-700" placeholder={`Search by job title`} value={searchTerm} onChange={(e) => handleInputChange(e.target.value)} onKeyDown={handleKeyInput} />
          <span
            className={`material-symbols-outlined tw-absolute tw-top-2 tw-right-2 tw-cursor-pointer`}
            onClick={() => {
              if (searchTerm === "") {
                return;
              }
              setSearchTerm("");
              handleSearchClick({ job_title: [] });
            }}>
            close
          </span>
        </div>
        {/* Search Button */}
        <button className={`btn btn-primary tw-h-8 tw-rounded-lg fetch-contact-button tw-flex tw-items-center tw-justify-center ${searchTerm === "" && !departments.length && !senority.length && !location.length ? "!tw-opacity-50 !tw-cursor-not-allowed" : ""}`} onClick={searchTerm === "" && !departments.length && !senority.length && !location.length ? () => null : handleSearchClick}>
          <span className="material-symbols-outlined">search</span>
        </button>
        <ContactFiltersDropdown
          sections={[
            {
              key: "department",
              overrideKey: "department",
              options: departmentOptions,
              value: departments,
              onChange: handleDepartmentChange,
              placeholder: "Search by department",
              displayName: "Department",
              maxSelections: 5,
              onClear: () => {
                setDepartments([]);
                handleSearchClick({ department: [] });
              },
            },
            {
              key: "seniority",
              overrideKey: "senority",
              options: seniorityOptions,
              value: senority,
              onChange: handleSeniorityChange,
              placeholder: "Search by management level",
              displayName: "Management Level",
              maxSelections: 5,
              onClear: () => {
                setSenority([]);
                handleSearchClick({ senority: [] });
              },
            },
            {
              key: "location",
              overrideKey: "location",
              options: locationList,
              value: location,
              onChange: handleLocationChange,
              placeholder: "Search by country",
              displayName: "Country",
              maxSelections: 5,
              onClear: () => {
                setLocation([]);
                handleSearchClick({ location: [] });
              },
            },
          ]}
          onApply={handleSearchClick}
          onClearAll={() => {
            setDepartments([]);
            setSenority([]);
            setLocation([]);
            handleSearchClick({ department: [], senority: [], location: [] });
          }}
        />
      </div>
    </div>
  );
};

export default NewContactForm;
